/**
 * `pads.module.css` — the PADS screen. `docs/design-direction.md` §9.9.
 *
 * The screen is two objects and nothing else: a solid `--accent` SIREN bar that is the
 * one loud thing on the page, and one dark plate with sixteen pads sunk into it. Every
 * other mark is a hairline or mono caps.
 *
 * Six things in here are load-bearing and must not be "tidied up":
 *
 *  1. **The pads are sunk INTO one plate, not laid out as sixteen tiles.** §12.13
 *     forbids two loud objects and sixteen bright tiles read as sixteen; the only
 *     framing that survives the rule is *one* plate with cells cut into it — the same
 *     move §9.4 already makes for the transport ("a strip, not floating circles"). The
 *     well is a ground step DOWN (`--recess` on `--bg-panel`) and each pad steps back
 *     UP out of it. That direction matters: `wiki/pages/pitch-next-attempt.md` measured
 *     that on a `--bg` ground you cannot mill a groove — a black ring on black resolves
 *     to one hairline — so depth here goes up, as a ground step plus a lit keyline.
 *  2. **The bevel is two 0-blur inset keylines, not a shadow.** Light on the top edge,
 *     shadow on the bottom, swapped on `:active`. §5.5 bans any blur over 3px and the
 *     licensed exception belongs to the mini player. A photorealistic rubber pad would
 *     be a soft shadow plus a glow plus a 100% radius — three separate bans in one
 *     graphic — so this reproduces the MATERIAL and refuses the MACHINE.
 *  3. **`--r-pad` is the one radius outside §5.3's closed set**, admitted by name for
 *     this file only in `stylelint.config.js`. See the token's own comment. It exists
 *     because the operator asked for the MPC look and a guillotine-cut corner does not
 *     read as a rubber pad. It must not spread to a second file.
 *  4. **`--pad` is a REMAINDER with a floor and a cap, never a share.**
 *     `wiki/pages/nowplaying-one-screen-budget.md` records that a proportional share was
 *     wrong in both directions, because every module but the subject costs a *fixed*
 *     number of pixels. And unlike `.halo` — whose remainder has no floor, which is why
 *     Now Playing's record reaches 0px under `--app-h` 500
 *     (`wiki/pages/responsive-coverage.md`, accepted) — this one has `--drop` as its
 *     floor, because a pad under 44px is not small, it is broken. The consequence is
 *     that this is the first screen in the app that may honestly scroll.
 *  5. **Buttons never scale** (§7.2). A press is a ground step and a light flip. Only
 *     `background`, `opacity` and `box-shadow` change, and `box-shadow` only between two
 *     blur-free inset pairs.
 *  6. **The readout lane never collapses.** `min-block-size` holds its line whether or
 *     not anything has been fired, so the plate does not jump on the first hit — the
 *     same rule §13 applies to every empty state.
 *
 * The bank is sized by the column and made square by `aspect-ratio`; its width is then
 * capped once, on `.screen`, by `--pad-cap` — a px length derived from `--app-h` and the
 * `--fixed-h` budget, floored at the 64px print floor and capped at 96. That is how the
 * whole screen fits without the `SEND` row falling off the bottom, and it is still not a
 * height remainder: height reaches the grid as an inline-axis cap only, so no percentage
 * crosses an axis. See `.bank`'s own comment for the trap that killed the first version.
 */

/* ────────────────────────────────────────────────────────────────────────────────
   THE PANEL — full column width, and the height budget that sets the pad.

   `.root` holds the budget so both the masthead and the plate can read it.

   **The screen never narrows.** The first fix capped the panel's WIDTH by the height
   budget and left the slack on the right; the operator's word for the result was
   *«справа пустое место почему то образовалось»*, and he was right — a bank 36px narrower
   than the title above it reads as a misprint, whichever edge the slack sits on.

   So width and height are now separate: the bank fills the column as it always did, and
   the height budget sets the pad's `block-size` directly. A pad is 89 × 80 instead of
   89 × 89 on a 430px phone — 10 % shorter, which nobody measures — and 0px of the column
   goes unused, which everybody sees.
   ──────────────────────────────────────────────────────────────────────────────── */

.root {
  /* The siren bar's height. Declared here, not on `.bank`, because `.siren` is a sibling
     of the bank and a custom property only inherits downwards. */
  --siren-h: 72px;

  /* A group rule is one 11px mono line at `line-height: 1.2` — 13.2px, rounded up. It is
     a literal because it is a measured line box, not a spacing choice. */
  --rule-h: 14px;

  /*
   * EVERY PIXEL ON THIS SCREEN THAT IS NOT THE BANK, as one number.
   *
   * Until 2026-09-06 the bank was sized by the column alone, and on a 430px phone the
   * stack came to 796px inside 678px of usable height: the `SEND` row and the readout
   * lane were both under the fold. They could not be scrolled to either — `touch-action:
   * none` on the siren and on the bank covers about 70 % of the viewport, which is
   * correct for playing and fatal for scrolling. §9.9 licenses this screen to scroll; it
   * does not license two of its six modules being unreachable.
   *
   * So the bank is now capped by what is LEFT, and this is the list of what takes the
   * rest, top to bottom: the masthead, the plate's own block padding, the siren, the two
   * group rules, the `SEND` row, the readout lane, the five gaps between the six
   * modules, the bank's own three gaps and two paddings, the four printed hairlines that
   * box the bank and the `SEND` row, and the 100px of dock the scroller reserves plus the
   * safe area (the same expression `states.module.css` pads with). 444px plus the inset.
   *
   * Every term is a length. No percentage appears anywhere in it — see `.bank` for the
   * axis trap that rule exists to avoid. Measured against the built screen at five
   * viewports on 2026-09-06; the readout lane lands 0–8px above the dock at every one of
   * them that can hold a printable bank.
   */
  --fixed-h: calc(
    var(--drop) + 2 * var(--s2) + 2 * var(--s4) + var(--siren-h) + 2 * var(--rule-h) + var(--drop) +
      var(--s6) + 5 * var(--s2) + 5 * var(--s2) + 4px + var(--drop) + var(--s3) + var(--drop) +
      env(safe-area-inset-bottom)
  );

  /*
   * `--pad-min` is 64px, not `--drop`. 44 is the legal touch floor; 64 is the PRINT floor
   * — the longest unbreakable legend is eight mono characters (`MELODICA`), 53px at
   * `--fs-11`, and 64 less two `--s1` of padding leaves 56. Below that the print clips,
   * which is exactly what shipped once and what the operator saw. A screen that scrolls
   * beats a pad that cannot print its own name, so the clamp floors here and the screen
   * is allowed to overrun on a phone too short to hold a printable bank.
   */
  --pad-min: var(--bar);
  --pad-max: 96px;

  /*
   * THE PAD'S HEIGHT — the only thing the budget sets. Floored at `--drop`, which is the
   * touch minimum; the print floor lives on the other axis and the column looks after it.
   */
  --pad-fit: calc((var(--app-h) - var(--fixed-h)) / 4);
  --pad-h: clamp(var(--drop), var(--pad-fit), var(--pad-max));
}

.screen {
  display: flex;
  flex-direction: column;
  gap: var(--s2);
}

/* ────────────────────────────────────────────────────────────────────────────────
   THE MASTHEAD — the title, the bank's name, and the way back, on one line.

   `Вернуть заводской` sits at the right end rather than under the title: as a third
   stacked line it cost 44px plus a gap, and those are the pixels the `SEND` row and the
   readout lane were missing (2026-09-06). It is also where a reset belongs — far from
   the pads, at the end of the line you read first.
   ──────────────────────────────────────────────────────────────────────────────── */

.masthead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s3);
  padding-inline: var(--gutter);
  padding-block: var(--s2);
}

.mastheadLines {
  min-inline-size: 0;
}

/* ────────────────────────────────────────────────────────────────────────────────
   THE SIREN — the one loud object (§9.9).

   Momentary: the voice lasts exactly as long as the finger is down. `touch-action:
   none` is mandatory here and the screen pairs it with `disableVerticalSwipes()` on
   every pointer-down — see the screen's own docblock for why the boot-time disable in
   `main.tsx` is not enough by the time a user reaches this screen.
   ──────────────────────────────────────────────────────────────────────────────── */

.siren {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s4);
  block-size: var(--siren-h);
  padding-inline: var(--s4);
  background: var(--accent);
  border: 0;
  border-radius: var(--r-cut);
  box-shadow: var(--raised-hi);
  touch-action: none;
  overflow: hidden;
  color: var(--text-on-accent);
}

/*
 * While held the ink steps to `--warm-peak`. That is the one place amber appears
 * outside the waveform's top stop, and it is licensed by being a live state rather
 * than decoration: the bar is the meter for its own voice.
 */
.siren[data-live='true'] {
  background: var(--meter-peak);
}

.sirenName {
  letter-spacing: 0.2em;
}

/* The hint is Russian, so no forced caps and no tracking — §4.3, §12.16. */
.sirenHint {
  text-align: end;
  opacity: 0.72;
}

/*
 * The thumb's index line, knocked out of the ink. Written as `--px` straight onto the
 * node by a pointer handler, never through React state: a playhead that goes through a
 * render always lags the finger (§7.2, the same rule the scrub's playhead follows).
 */
.sirenIndex {
  position: absolute;
  inset-block: 0;
  inline-size: 2px;
  background: var(--ink-000);
  opacity: 0;
  transform: translateX(var(--px, 0));
  pointer-events: none;
}

.siren[data-live='true'] .sirenIndex {
  opacity: 0.85;
}

/*
 * The one-drop ruler along the bar's bottom edge, doing its sixth job: the pitch scale.
 * Every third tick is the accent, so the mark that means "the third beat" everywhere
 * else in the app also means "a whole tone" here.
 *
 * The ticks themselves are the shared `Ruler` primitive (§6.6) — this block only places
 * it and re-inks it. Rolling a second tick strip here would be exactly the drift
 * `Ruler`'s own header warns about, and its `barTicks()` rounding is why the count is 87
 * (a whole number of three-tick bars) rather than a round 90.
 *
 * Knocked out in the ink rather than printed in it, because the bar is already
 * `--accent`: `--border-strong` on orange is invisible and `--accent` on `--accent` is
 * nothing at all.
 */
.sirenScale {
  position: absolute;
  inset-inline: var(--s4);
  inset-block-end: var(--s2);
  pointer-events: none;
}

.sirenScale i {
  background: var(--ink-000);
  opacity: 0.35;
}

.sirenScale i:nth-child(3n) {
  background: var(--ink-000);
  opacity: 0.6;
}

/* ────────────────────────────────────────────────────────────────────────────────
   Group rules. The silkscreen legend sits ON the bezel line, which §6.12 calls the
   cheapest device in the system for making a page read as an object.
   ──────────────────────────────────────────────────────────────────────────────── */

.groupRule {
  display: flex;
  align-items: center;
  gap: var(--s2);
}

.groupRule .line {
  flex: 1;
  block-size: 1px;
  background: var(--border);
}

/* The one-drop bar: `--drop` wide, `--accent`, on the third beat of the rule. */
.groupRule .drop {
  inline-size: var(--drop);
  block-size: 2px;
  background: var(--accent);
}

/* ────────────────────────────────────────────────────────────────────────────────
   THE BANK — one plate, sixteen pads sunk into it.
   ──────────────────────────────────────────────────────────────────────────────── */

.bank {
  /*
   * SIZED BY THE COLUMN, MADE SQUARE BY `aspect-ratio` — and NOT by a height remainder.
   *
   * The first version computed one `--pad` length from `min(width-term, height-term)` and
   * fed it to both `grid-template-columns` and `grid-auto-rows`. That is a trap: the
   * `100%` inside the width term resolves against the **inline** axis in one and against
   * the **block** axis in the other, so `grid-auto-rows` silently became
   * `min(25% - 10px, 64px, 96px)` and every pad computed 64 × 36 — a third of its height
   * gone and well under the 44px touch floor. Measured in the built bundle, not deduced.
   *
   * So the width comes from the grid (`1fr` columns, floored at `--pad-min`) and the
   * height comes from `--pad-h`, a px length derived from `--app-h` and applied to the
   * pad's own `block-size`. Two axes, two independent numbers, no percentage in either —
   * which is exactly what the first version failed to keep apart.
   */
  display: grid;
  grid-template-columns: repeat(4, minmax(var(--pad-min), 1fr));
  /* `--s2` written straight into `gap` and `padding` rather than through a screen-owned
     alias: `bl/spacing-closed-set` reads the literal declaration and does not follow an
     indirection — correctly, since an indirection is exactly how a 6px gap would get in. */
  gap: var(--s2);
  padding: var(--s2);
  /* Four pads at the print cap, their three gaps and the well's own padding. Keeps a
     desktop column from printing four slabs. */
  max-inline-size: calc(4 * var(--pad-max) + 3 * var(--s2) + 2 * var(--s2));
  background: var(--bg-panel);
  border: 1px solid var(--border-strong);
  box-shadow: var(--recess), var(--raised-hi);
  touch-action: none;
}

.pad {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  /*
   * The height is the budget's, not the width's. It was `aspect-ratio: 1` until
   * 2026-09-06, and a square pad meant the bank's height was the column's width — so the
   * only way to make the screen fit was to narrow the whole panel, which left a hole down
   * the right-hand side. `--pad-h` is already floored at `--drop`, so the touch minimum
   * the `aspect-ratio` line used to guard is guarded here.
   */
  block-size: var(--pad-h);
  padding: var(--s1);
  text-align: start;
  color: var(--text);
  background: linear-gradient(180deg, #272219 0%, #1c1815 58%, #141210 100%);
  border: 1px solid #090807;
  border-radius: var(--r-pad);
  box-shadow:
    inset 0 1px 0 rgba(239, 232, 218, 0.11),
    inset 0 -1px 0 rgba(0, 0, 0, 0.6);
  touch-action: none;
}

/* Pressed: the pad goes down into the well and the light moves to its underside. */
.pad:active {
  background: linear-gradient(180deg, #131110 0%, #1d1916 100%);
  box-shadow:
    inset 0 2px 0 rgba(0, 0, 0, 0.62),
    inset 0 -1px 0 rgba(239, 232, 218, 0.05);
}

/*
 * Struck: the INKING move (§7.2) — the ink floods and the glyph knocks out. This is
 * this system's lit MPC pad, and it is held for the sound's own measured duration
 * rather than a fixed 100ms, so the light IS the length of the sound.
 * Declared after `:active` on purpose: the two selectors are the same specificity and
 * a struck pad under a resting thumb must read as struck.
 */
.pad.lit {
  color: var(--text-on-accent);
  background: linear-gradient(180deg, #ff6a3c 0%, var(--accent) 56%, #c93c17 100%);
  border-color: #8e2a0f;
  box-shadow:
    inset 0 1px 0 rgba(255, 250, 240, 0.3),
    inset 0 -1px 0 rgba(0, 0, 0, 0.42);
}

/* An unassigned pad is dark, dashed and says nothing. §12.8: no illustration, no copy. */
.pad.empty {
  background: var(--bg-raised);
  border-style: dashed;
  border-color: var(--border);
  box-shadow: none;
}

.padSlot {
  color: var(--text-3);
}

.pad.lit .padSlot {
  color: var(--text-on-accent);
  opacity: 0.72;
}

/*
 * The legend is `--text`, never `Silkscreen`'s `--text-3`: §3.3 licenses 4.66:1 for
 * non-interactive metadata only, and a pad's name is the most actionable label on the
 * screen.
 *
 * **One tier on every device**, `--fs-11` — the silkscreen tier §6.12 prescribes for
 * panel print anyway. The first version dropped from `--fs-13` to `--fs-11` only for
 * legends over seven characters, so `COWBELL` and `X-STICK` (seven each) stayed at 13px,
 * overflowed a narrow pad and were clipped — and the screen printed two different sizes
 * on a desktop and on a phone. A legend that changes size with the window is a legend the
 * operator has to learn twice.
 *
 * `keep-all` forbids a mid-word break: `MELODIC/A` is worse than a small font. A
 * two-word legend wraps at its space; a one-word legend relies on the 64px floor above.
 */
.pad .padLabel {
  /*
   * NO TRACKING, and that is what makes the 64px print floor above a true statement
   * rather than an aspiration.
   *
   * The floor was computed from `MELODICA` at 53px — the width of eight mono characters
   * at `--fs-11` and nothing else. `.t-11` then adds §4.3's 0.16em, which is 1.76px per
   * character, so the legend actually measured 66.9px and needed a 77px pad. Measured at
   * five viewports on 2026-09-06: it clipped on every phone narrower than 393px, and it
   * clipped again the moment the bank was capped by height. Two other legends
   * (`WHISTLE`, `COWBELL`) were within 1.5px of the same fate.
   *
   * The tracking stays on `.padSlot` — `A01` is three characters and can never overrun,
   * and the silkscreen numbering is where the letterpress reading comes from anyway. The
   * legend is a label on a grid-sized box, and a box the grid sizes cannot also be sized
   * by the copy inside it.
   *
   * `.pad .padLabel` and not `.padLabel`: `index.css` is imported last, so a module class
   * loses to an equal-specificity global one — `.t-11`'s own `letter-spacing` would win.
   * See `wiki/pages/css-modules-lose-to-global-classes.md`. Two classes, 0-2-0, done.
   */
  letter-spacing: normal;
  overflow-wrap: normal;
  word-break: keep-all;
  line-height: 1.05;
}

/* ────────────────────────────────────────────────────────────────────────────────
   SEND — two cells on the same plate material. `ECHO` latches, `MUTE` does not.
   ──────────────────────────────────────────────────────────────────────────────── */

.sends {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  background: var(--bg-panel);
  border: 1px solid var(--border-strong);
  box-shadow: var(--recess), var(--raised-hi);
}

/* Separation is a printed rule, never a gap (§5.4). A 1px gap would also be a spacing
   value outside the closed set, which is the lint rule doing its job. */
.send + .send {
  border-inline-start: 1px solid var(--border);
}

.send {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s2);
  min-block-size: var(--drop);
  padding-inline: var(--s3);
  color: var(--text);
  background: var(--bg-raised);
  border: 0;
  border-radius: var(--r-cut);
  box-shadow: var(--raised-hi);
  touch-action: none;
}

.send:active {
  background: var(--bg-pressed);
}

.send.lit {
  color: var(--text-on-accent);
  background: var(--accent);
}

.sendHint {
  color: var(--text-3);
}

.send.lit .sendHint {
  color: var(--text-on-accent);
  opacity: 0.72;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The readout lane. One line, Russian, naming what was just fired — real event data,
   and the only Russian on the object. It holds its height from the first frame.
   ──────────────────────────────────────────────────────────────────────────────── */

/*
 * `Вернуть заводской` — a word in a square stamp bed, per §12.21 (actions are words,
 * never an icon). It sits on the masthead line rather than on the plate: it is a thing
 * you do to the bank, not a control you play.
 */
.reset {
  flex: none;
  min-block-size: var(--drop);
  padding-inline: var(--s3);
  color: var(--text-2);
  background: var(--bg-raised);
  border: 1px solid var(--border);
  border-radius: var(--r-cut);
  letter-spacing: 0;
  text-transform: none;
}

.reset:active {
  color: var(--text-on-accent);
  background: var(--accent);
  border-color: var(--accent);
}

.readout {
  min-block-size: var(--s6);
  color: var(--accent-quiet);
}

/* ────────────────────────────────────────────────────────────────────────────────
   The library, inside a §9.8 sheet. A ledger of rows, not a grid of cards: tapping a
   row auditions it, and the row's own stamped control assigns it.
   ──────────────────────────────────────────────────────────────────────────────── */

.libFilters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--s2);
  padding-block-end: var(--s3);
  border-block-end: 1px solid var(--border);
}

.libList {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.libRow {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: var(--s3);
  min-block-size: var(--drop);
  padding-block: var(--s2);
  border-block-end: 1px solid var(--border);
}

/* Every fourth row takes the emphasis tier — §5.4's bar line, which is what makes a
   long list read as ruled paper instead of as a scroll of rows. */
.libRow:nth-child(4n) {
  border-block-end-color: var(--border-strong);
}

.libRow.current {
  background: var(--accent-tint);
}

.libAudition {
  display: flex;
  flex-direction: column;
  gap: var(--s1);
  min-block-size: var(--drop);
  padding: 0;
  text-align: start;
  color: inherit;
  background: none;
  border: 0;
}

.libLabel {
  color: var(--text);
}

.libRu {
  color: var(--text-2);
}

.libAssign {
  min-block-size: var(--drop);
  min-inline-size: var(--drop);
  padding-inline: var(--s3);
  color: var(--text);
  background: var(--bg-raised);
  border: 1px solid var(--border-strong);
  border-radius: var(--r-cut);
  box-shadow: var(--raised-hi);
}

.libAssign:active {
  color: var(--text-on-accent);
  background: var(--accent);
  border-color: var(--accent);
}

/* The licence line: CC0 and the author, printed once per row. Not a legal obligation —
   the library is CC0 precisely so there is none — but the provenance of every sound in
   the product is worth being able to read off the screen. */
.libLicence {
  grid-column: 1 / -1;
  color: var(--text-3);
}

/* ────────────────────────────────────────────────────────────────────────────────
   320px. The gutter is already `--s3` below 360 (`tokens.css`); the well tightens too,
   or four 44px pads plus their gaps do not fit the column.
   ──────────────────────────────────────────────────────────────────────────────── */

@media (max-width: 359px) {
  .bank {
    gap: var(--s1);
    padding: var(--s1);
  }

  /* Recomputed with the tighter well, so the panel cap still describes the plate under
     it. The cap is unreachable at this width anyway — the column binds first — but
     leaving the wide figure here would be a number that lies. */
  .bank {
    max-inline-size: calc(4 * var(--pad-max) + 3 * var(--s1) + 2 * var(--s1));
  }
}
